import {
  defaultHashSha256,
  getAssumedMasterState,
  getChangedDocumentsSince,
  getHeightOfRevision,
  getLastCheckpointDoc,
  type RxCollection,
  sortObject,
  writeDocToDocState,
} from 'rxdb';
import type { RxReplicationState } from 'rxdb/plugins/replication';

const CHANGES_BATCH_SIZE = 200;

/**
 * Counts the local writes a replication has not pushed yet, read from its persisted upstream
 * checkpoint — the position RxDB resumes pushing from after a reload — rather than from any
 * app-level flag, so it holds while the push keeps failing and across reloads.
 *
 * A changed document is skipped by the same test RxDB's upstream uses before pushing
 * (replication-protocol/upstream.js, rxdb 17.4.0): it equals the assumed master state the meta
 * instance recorded when it was pulled or last pushed. That is what keeps pulled documents out of the
 * count — the storage wrapper replaces `_meta` on insert, so no pulled-document marker survives on
 * the document itself.
 *
 * Checkpoint and meta writes are queued, not awaited, by the replication, so a document written or
 * pushed a moment ago can still be counted — the error only ever leans towards "unsynced".
 *
 * Documents another, pull-only replication of the same collection wrote (the archive partition) are
 * the server's state, but this upstream has no record of them: a live one waits for a redundant
 * re-push, and a tombstone is stored without a change event (rx-storage-helper.js
 * categorizeBulkWriteRows), so this upstream only reaches it on its next initial sync. Two tests keep
 * them out of the count, both only for a document this upstream has no assumed master for:
 * - `pulledFingerprints` (document id → fingerprintServerState of what that pull wrote): a document
 *   whose state still has that fingerprint has not changed since. A local edit or removal changes the
 *   state; a duplicate gets a new id. Content, not `_rev`: the storage replaces the revision the
 *   replication hands it. A fingerprint can go stale (it is not refreshed when a pull finds the
 *   document unchanged), which is safe only because of that precondition: without an assumed master
 *   nothing local has reached the server since the pull, so matching content has nothing to lose.
 *   Accepted gap: a leader that goes away between writing a re-pulled document and saving its new
 *   fingerprint leaves a stored one older than that pull. Pending fingerprints are saved when the
 *   page is hidden or unloaded, so what can still be missing is a document whose fingerprint was
 *   still being computed, or everything pending when the tab dies without either event.
 *   Anything pushed has an assumed master, which then decides as for any other document — a change
 *   pushed and then reverted to the pulled content is still counted.
 * - A deleted document at revision 1 with no assumed master was inserted already deleted, which only
 *   a pull does — a local removal always updates an existing document. This one needs no record of
 *   the pull, e.g. for one a previous session pulled that `pulledFingerprints` does not cover.
 *
 * Resolves undefined when the replication has not started yet: there is no state to read.
 */
export async function countUnpushedWrites(
  replicationState: RxReplicationState<any, any>,
  pulledFingerprints?: ReadonlyMap<string, string>
): Promise<number | undefined> {
  const state = replicationState.internalReplicationState;
  if (!state) {
    return undefined;
  }

  const { forkInstance, identifier, conflictHandler, keepMeta } = state.input;
  const primaryPath = state.primaryPath;
  let checkpoint = await getLastCheckpointDoc(state, 'up');
  let count = 0;

  for (;;) {
    const changes = await getChangedDocumentsSince(forkInstance, CHANGES_BATCH_SIZE, checkpoint);
    const assumedMasterState = await getAssumedMasterState(
      state,
      changes.documents.map((doc) => doc[primaryPath] as string)
    );

    const pulledUnchanged = new Set<string>();
    await Promise.all(
      changes.documents.map(async (fullDocData) => {
        const id = fullDocData[primaryPath] as string;
        const pulledFingerprint = pulledFingerprints?.get(id);
        if (!assumedMasterState[id] && pulledFingerprint && pulledFingerprint === (await fingerprintServerState(fullDocData))) {
          pulledUnchanged.add(id);
        }
      })
    );

    count += changes.documents.filter((fullDocData) => {
      const id = fullDocData[primaryPath] as string;
      if (pulledUnchanged.has(id)) {
        return false;
      }

      const assumedMasterDoc = assumedMasterState[id];
      if (!assumedMasterDoc) {
        const insertedAlreadyDeleted = fullDocData._deleted && getHeightOfRevision(fullDocData._rev) === 1;
        return !insertedAlreadyDeleted;
      }

      const docData = writeDocToDocState(fullDocData, state.hasAttachments, !!keepMeta);
      const equalsMaster =
        assumedMasterDoc.metaDocument.isResolvedConflict !== fullDocData._rev &&
        conflictHandler.isEqual(assumedMasterDoc.docData, docData, 'upstream-check-if-equal');
      const revisionMatchesMaster =
        !!assumedMasterDoc.docData._rev && getHeightOfRevision(fullDocData._rev) === fullDocData._meta[identifier];

      return !equalsMaster && !revisionMatchesMaster;
    }).length;

    if (changes.documents.length < CHANGES_BATCH_SIZE) {
      return count;
    }
    checkpoint = changes.checkpoint;
  }
}

/**
 * For a collection no replication can be read for (not started, e.g. a fresh sign-in held at the
 * agreements gate): nothing is unpushed only when it stores no document at all, tombstones included.
 * Otherwise undefined, since the local database is shared by every account signed in on this browser
 * and can hold an earlier session's unpushed writes.
 */
export async function countUnpushedWritesWithoutReplication(
  collection: RxCollection<any, unknown, unknown, unknown>
): Promise<0 | undefined> {
  const { documents } = await getChangedDocumentsSince(collection.storageInstance, 1);
  return documents.length ? undefined : 0;
}

// Identifies a document's replicated state: what the server holds, without the local-only revision,
// meta and attachment bookkeeping. Keys are sorted so the same state always hashes the same.
export async function fingerprintServerState(doc: { _rev?: string; _meta?: unknown; _attachments?: unknown }): Promise<string> {
  const { _rev, _meta, _attachments, ...state } = doc;
  return defaultHashSha256(JSON.stringify(sortObject(state, true)));
}
